<ion-content>
  <div class="constrained-content">
    <ng-container *ngIf="authService.integrations?.slackEnabled && onboardingProgress" style="display: flex">
      <ng-container>
        <div style="text-align: center; margin: 60px 0 30px 0" class="gradient-text">
          <h1 style="font-size: 1.6rem">
            <ion-icon name="rocket"></ion-icon>
            {{ isDone ? "You're all set!" : 'Get your account ready for prime time' }}
          </h1>
        </div>
        <h4 style="margin: 40px 10px 20px 10px">Quickstart Guide:</h4>
        <ion-card>
          <ion-accordion-group #accordionGroup>
            <ion-accordion value="slack">
              <ion-item slot="header">
                <ion-icon
                  [name]="onboardingProgress.isSlackConnected ? 'checkmark-circle' : 'checkmark-circle-outline'"
                  class="icon-padded"
                  [ngClass]="{ green: true }"
                ></ion-icon>
                <ion-label class="step-label" [ngClass]="{ done: onboardingProgress.isSlackConnected }">
                  Connect to Slack
                </ion-label>
              </ion-item>
              <div class="step-content" slot="content">
                <ion-icon name="checkmark-outline" class="padded-icon"></ion-icon>
                Nice! You're all set. This is an important one, so pat yourself on the back.
              </div>
            </ion-accordion>
            <ion-accordion value="customer">
              <ion-item slot="header">
                <ion-icon
                  [name]="onboardingProgress.isCustomerAdded ? 'checkmark-circle' : 'checkmark-circle-outline'"
                  class="icon-padded"
                  [ngClass]="{ green: true }"
                ></ion-icon>
                <ion-label class="step-label" [ngClass]="{ done: onboardingProgress.isCustomerAdded }">
                  Choose Channels to Track
                </ion-label>
              </ion-item>
              <div class="step-content" slot="content">
                Add Unthread to your customer channels and we'll start tracking conversations behind the scenes.
                <ion-button (click)="addCustomers()" style="margin-top: 15px">
                  <ion-icon name="add-outline" class="padded-icon"></ion-icon>Connect a channel
                </ion-button>
              </div>
            </ion-accordion>
            <ion-accordion value="triage">
              <ion-item slot="header">
                <ion-icon
                  [name]="onboardingProgress.isTriageChannelSet ? 'checkmark-circle' : 'checkmark-circle-outline'"
                  class="icon-padded"
                  [ngClass]="{ green: true }"
                ></ion-icon>
                <ion-label class="step-label" [ngClass]="{ done: onboardingProgress.isTriageChannelSet }">
                  Set up Triage Channel
                </ion-label>
              </ion-item>
              <div class="step-content" slot="content">
                The triage channel is where you can
                <span class="highlighted">cross-post customer conversations for internal discussion.</span>
                <p>You can also set a custom triage channel per customer.</p>

                <app-channel-selector
                  [channel]="triageChannel"
                  (channelDidChange)="onTriageChannelChange($event)"
                  [defaultInternalOnly]="true"
                ></app-channel-selector>

                <div class="flex-space-between" style="margin-top: 15px">
                  <ion-button
                    color="success"
                    (click)="saveTriageChanges()"
                    [disabled]="isLoading || !defaultTriageChannelId"
                  >
                    <span [hidden]="isLoading">Save</span>
                    <ion-spinner name="crescent" *ngIf="isLoading"></ion-spinner>
                  </ion-button>
                  <ion-button fill="clear" href="https://docs.unthread.io/conversations/triaging" target="_blank">
                    <ion-icon name="book-outline" class="padded-icon"></ion-icon>Learn more
                  </ion-button>
                </div>
              </div>
            </ion-accordion>
            <ion-accordion value="assignment">
              <ion-item slot="header">
                <ion-icon
                  [name]="onboardingProgress.isAssignmentRuleSet ? 'checkmark-circle' : 'checkmark-circle-outline'"
                  class="icon-padded"
                  [ngClass]="{ green: true }"
                ></ion-icon>
                <ion-label class="step-label" [ngClass]="{ done: onboardingProgress.isAssignmentRuleSet }">
                  Set Default SLA
                </ion-label>
              </ion-item>
              <div class="step-content" slot="content">
                Determine how new messages are <span class="highlighted">assigned, escalated and triaged.</span>
                <p>Add multiple steps that will run until someone responds.</p>
                <app-support-steps
                  [isDefaults]="true"
                  [initialSupportSteps]="defaultSupportSteps"
                  (didChange)="defaultSupportStepsDidChange($event)"
                ></app-support-steps>
                <div class="flex-space-between">
                  <ion-button color="success" (click)="saveSupportSteps()" [disabled]="isLoading">
                    <span [hidden]="isLoading">Save</span>
                    <ion-spinner name="crescent" *ngIf="isLoading"></ion-spinner>
                  </ion-button>
                  <ion-button fill="clear" href="https://docs.unthread.io/conversations/slas" target="_blank">
                    <ion-icon name="book-outline" class="padded-icon"></ion-icon>Learn more
                  </ion-button>
                </div>
              </div>
            </ion-accordion>
            <ion-accordion value="team">
              <ion-item slot="header">
                <ion-icon
                  [name]="onboardingProgress.isAdminAdded ? 'checkmark-circle' : 'checkmark-circle-outline'"
                  class="icon-padded"
                  [ngClass]="{ green: true }"
                ></ion-icon>
                <ion-label class="step-label" [ngClass]="{ done: onboardingProgress.isAdminAdded }">
                  Invite a Team Member
                </ion-label>
              </ion-item>
              <div class="step-content" slot="content">
                Unthread is better with friends. Send an invitation to access the dashboard:
                <br />
                <div style="margin-top: 15px">
                  <ion-button (click)="inviteTeamMember(true)"> Invite an account admin </ion-button>
                  <ion-button fill="clear" (click)="saveSkippedStep('isAdminAdded')"> Skip this </ion-button>
                </div>
              </div>
            </ion-accordion>
          </ion-accordion-group>
        </ion-card>

        <h4 style="margin: 40px 10px 20px 10px">Add AI and Automations:</h4>
        <ion-card>
          <ion-accordion-group #aiAccordion>
            <ion-accordion *ngFor="let step of aiOnboardingSteps" [value]="step.key">
              <ion-item slot="header">
                <ion-icon
                  [name]="onboardingProgress[step.key] ? 'checkmark-circle' : 'checkmark-circle-outline'"
                  class="green icon-padded"
                ></ion-icon>
                <ion-label class="step-label" [ngClass]="{ done: onboardingProgress[step.key] }">
                  {{ step.title }}
                </ion-label>
              </ion-item>
              <div class="step-content" slot="content">
                <p style="margin-top: 0">{{ step.description }}</p>
                <div class="flex-space-between">
                  <div>
                    <ion-button [routerLink]="step.buttonPath">
                      {{ step.buttonTitle }}
                    </ion-button>
                    <ion-button fill="clear" (click)="saveSkippedStep(step.key)"> Skip this </ion-button>
                  </div>
                  <ion-button fill="clear" href="{{ step.docs }}" target="_blank">
                    <ion-icon name="book-outline" class="padded-icon"></ion-icon>Learn more
                  </ion-button>
                </div>
              </div>
            </ion-accordion>
          </ion-accordion-group>
        </ion-card>

        <h4 style="margin: 40px 10px 20px 10px">Advanced Account Setup:</h4>
        <ion-card>
          <ion-accordion-group #advancedAccordion>
            <ion-accordion value="tasks">
              <ion-item slot="header">
                <ion-icon
                  [name]="onboardingProgress.isTaskManagerConnected ? 'checkmark-circle' : 'checkmark-circle-outline'"
                  class="icon-padded"
                  [ngClass]="{ green: true }"
                ></ion-icon>
                <ion-label class="step-label" [ngClass]="{ done: onboardingProgress.isTaskManagerConnected }">
                  Connect Your Task Manager
                </ion-label>
              </ion-item>
              <div class="step-content" slot="content">
                Link tasks in <span class="highlighted">Jira, Asana, Linear, and many more</span> to your conversations
                in Unthread.
                <p>Track task statuses from your dashbaord and get notified when tasks are completed.</p>
                <div class="flex-space-between">
                  <div>
                    <ion-button (click)="setupTaskManager()">
                      <ion-icon name="settings-outline" class="padded-icon"></ion-icon> Connect Your Task Manager
                    </ion-button>
                    <ion-button fill="clear" (click)="saveSkippedStep('isTaskManagerConnected')">
                      Skip this
                    </ion-button>
                  </div>
                  <ion-button fill="clear" href="https://docs.unthread.io/integrations/task-managers" target="_blank">
                    <ion-icon name="book-outline" class="padded-icon"></ion-icon>Learn more
                  </ion-button>
                </div>
              </div>
            </ion-accordion>
            <ion-accordion value="crm">
              <ion-item slot="header">
                <ion-icon
                  [name]="onboardingProgress.isCRMConnected ? 'checkmark-circle' : 'checkmark-circle-outline'"
                  class="icon-padded"
                  [ngClass]="{ green: true }"
                ></ion-icon>
                <ion-label class="step-label" [ngClass]="{ done: onboardingProgress.isCRMConnected }">
                  Connect Your CRM
                </ion-label>
              </ion-item>
              <div class="step-content" slot="content">
                Connect your <span class="highlighted">Hubspot or Salesforce</span> account to keep conversations in
                sync.
                <p>
                  You can sync conversation history back to the CRM as well as sync primary and secondary account reps
                  from their account profile.
                </p>
                <div class="flex-space-between">
                  <div>
                    <ion-button (click)="setupCRM()">
                      <ion-icon name="settings-outline" class="padded-icon"></ion-icon> Connect Your CRM
                    </ion-button>
                    <ion-button fill="clear" (click)="saveSkippedStep('isCRMConnected')"> Skip this </ion-button>
                  </div>
                  <ion-button fill="clear" href="https://docs.unthread.io/integrations/salesforce" target="_blank">
                    <ion-icon name="book-outline" class="padded-icon"></ion-icon>Learn more
                  </ion-button>
                </div>
              </div>
            </ion-accordion>
            <ion-accordion value="email">
              <ion-item slot="header">
                <ion-icon
                  [name]="onboardingProgress.isEmailConnected ? 'checkmark-circle' : 'checkmark-circle-outline'"
                  class="icon-padded"
                  [ngClass]="{ green: true }"
                ></ion-icon>
                <ion-label class="step-label" [ngClass]="{ done: onboardingProgress.isEmailConnected }">
                  Enable Email Support
                </ion-label>
              </ion-item>
              <div class="step-content" slot="content">
                Email integration <span class="highlighted">relays messages between email and Slack.</span>
                <p>Connect a support email, and you can read and respond right from Slack.</p>
                <div class="flex-space-between">
                  <div>
                    <ion-button (click)="setupEmail()">
                      <ion-icon name="settings-outline" class="padded-icon"></ion-icon> Set up email
                    </ion-button>
                    <ion-button fill="clear" (click)="saveSkippedStep('isEmailConnected')"> Skip this </ion-button>
                  </div>
                  <ion-button fill="clear" href="https://docs.unthread.io/email/email-slack-connector" target="_blank">
                    <ion-icon name="book-outline" class="padded-icon"></ion-icon>Learn more
                  </ion-button>
                </div>
              </div>
            </ion-accordion>
            <ion-accordion value="widget">
              <ion-item slot="header">
                <ion-icon
                  [name]="onboardingProgress.isWidgetConnected ? 'checkmark-circle' : 'checkmark-circle-outline'"
                  class="icon-padded"
                  [ngClass]="{ green: true }"
                ></ion-icon>
                <ion-label class="step-label" [ngClass]="{ done: onboardingProgress.isWidgetConnected }">
                  Embed the {{ isInternalSupport ? 'Customer Portal' : 'In-app Chat Widget' }}
                </ion-label>
              </ion-item>
              <div class="step-content" slot="content">
                The {{ isInternalSupport ? 'Customer Portal' : 'In-app Chat Widget' }} allows you to
                <span class="highlighted">embed live chat</span> into your web application.
                <p>New tickets will be posted to a Slack channel, and you can respond right from there.</p>
                <div class="flex-space-between">
                  <div>
                    <ion-button (click)="setupWidget()">
                      <ion-icon name="settings-outline" class="padded-icon"></ion-icon> Set up
                    </ion-button>
                    <ion-button fill="clear" (click)="saveSkippedStep('isWidgetConnected')"> Skip this </ion-button>
                  </div>
                  <ion-button fill="clear" href="https://docs.unthread.io/in-app/chat-widget" target="_blank">
                    <ion-icon name="book-outline" class="padded-icon"></ion-icon>Learn more
                  </ion-button>
                </div>
              </div>
            </ion-accordion>
            <ion-accordion value="integrations">
              <ion-item slot="header">
                <ion-icon
                  [name]="onboardingProgress.isIntegrationAdded ? 'checkmark-circle' : 'checkmark-circle-outline'"
                  class="icon-padded"
                  [ngClass]="{ green: true }"
                ></ion-icon>
                <ion-label class="step-label" [ngClass]="{ done: onboardingProgress.isIntegrationAdded }">
                  Add 3rd Party Integrations
                </ion-label>
              </ion-item>
              <div class="step-content" slot="content">
                Set up integrations with 3rd party systems through <span class="highlighted">Zapier or our API.</span>
                <p>New tickets will be posted to a Slack channel, and you can respond right from there.</p>
                <div class="flex-space-between">
                  <div>
                    <ion-button (click)="setupIntegrations()">
                      <ion-icon name="settings-outline" class="padded-icon"></ion-icon> Set up integrations
                    </ion-button>
                    <ion-button fill="clear" (click)="saveSkippedStep('isIntegrationAdded')"> Skip this </ion-button>
                  </div>
                  <ion-button fill="clear" href="https://docs.unthread.io/api-introduction/using-api" target="_blank">
                    <ion-icon name="book-outline" class="padded-icon"></ion-icon>Learn more
                  </ion-button>
                </div>
              </div>
            </ion-accordion>
          </ion-accordion-group>
        </ion-card>
      </ng-container>
      <div style="margin: 40px 0; display: flex; justify-content: center; flex-direction: column; text-align: center">
        <p style="font-size: 0.8rem; opacity: 0.7">Still need help?</p>
        <ion-button fill="clear" href="https://calendly.com/tom-unthread/30min" target="_blank">
          <ion-icon name="calendar-outline" class="padded-icon"></ion-icon>
          Book an onboarding call
        </ion-button>
        <ion-button fill="clear" href="https://docs.unthread.io/introduction" target="_blank">
          <ion-icon name="book-outline" class="padded-icon"></ion-icon>
          Read the Helpdesk
        </ion-button>
      </div>
    </ng-container>

    <div *ngIf="!authService.integrations?.slackEnabled">
      <div style="text-align: center">
        <div
          style="
            width: 300px;
            display: flex;
            height: 210px;
            border-radius: 18px;
            margin: 40px auto;
            justify-content: center;
            align-items: center;
            flex-direction: column;
            background-image: url('https://assets.unthread.io/assets/imgs/chat-flow.png');
            background-repeat: no-repeat;
            background-size: cover;
            background-color: #efefff;
            color: var(--ion-color-primary);
            cursor: pointer;
          "
          (click)="playVideo()"
        >
          <ion-icon name="play-circle-outline" style="font-size: 4rem"></ion-icon>
        </div>
        <div class="gradient-text">
          <h1 style="font-size: 2.2rem">👋 Welcome to Unthread</h1>
          <p>Unthread is an AI-powered helpdesk, built into Slack.</p>
          <p>Automatically track conversations across Slack, email, in-app chat, and more.</p>
        </div>
        <ion-button class="btn-large" style="margin-top: 20px" (click)="installSlack()">
          <ion-icon name="logo-slack" class="padded-icon"></ion-icon>
          Install the app for Slack
        </ion-button>
        <p style="font-size: 0.8rem; opacity: 0.8">
          By installing the app, you agree to our
          <a href="https://unthread.io/privacy" target="_blank">Privacy Policy</a> and
          <a href="https://unthread.io/terms" target="_blank">Terms of Service</a>
        </p>
      </div>
    </div>
  </div>
</ion-content>
